<script setup lang="ts">
import { computed, ref } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Popover from '@uiKit/elements/Popover.vue'
import BreakdownPopover from '@uiKit/components/BreakdownPopover.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import { formatAed, formatTokenPrice } from '@shared/utils'

interface FeeBreakdown {
  dldFee: number
  acquisitionFee: number
  tokenisationFee: number
  platformFee: number
}

interface Props {
  tokenPrice: number
  grossAmount: number
  totalFeeAmount: number
  netAmount: number
  platformFeeAmount: number
  dldFeeAmount: number
  acquisitionFeeAmount: number
  tokenisationFeeAmount: number
  feesBreakdown?: FeeBreakdown
  exceedsTokens: boolean
  tokensAvailable: number
  isFetching: boolean
}

const props = defineProps<Props>()

const tokenFormatter = new Intl.NumberFormat('en-US')

const feePopover = ref<InstanceType<typeof Popover> | null>(null)

const feeRows = computed(() => {
  if (props.feesBreakdown) {
    return [
      {
        label: `DLD Fee (${props.feesBreakdown.dldFee}%)`,
        value: formatAed(props.dldFeeAmount),
      },
      {
        label: `Acquisition Fee (${props.feesBreakdown.acquisitionFee}%)`,
        value: formatAed(props.acquisitionFeeAmount),
      },
      {
        label: `Tokenisation Fee (${props.feesBreakdown.tokenisationFee}%)`,
        value: formatAed(props.tokenisationFeeAmount),
      },
      {
        label: 'Platform Fee',
        value: formatAed(props.platformFeeAmount),
      },
    ]
  }
  return [
    { label: 'DLD Fee', value: formatAed(props.dldFeeAmount) },
    { label: 'Acquisition Fee', value: formatAed(props.acquisitionFeeAmount) },
    { label: 'Tokenisation Fee', value: formatAed(props.tokenisationFeeAmount) },
    { label: 'Platform Fee', value: formatAed(props.platformFeeAmount) },
  ]
})
</script>

<template>
  <Box direction="col" class="summary" :class="{ 'summary--fetching': isFetching }">
    <Box align="center" justify="between" gap="2" class="summary__row">
      <Text type="body-sm" is="span">Price Per Token</Text>
      <Text type="body-sm" weight="semibold" is="span">{{ formatTokenPrice(tokenPrice) }}</Text>
    </Box>
    <Box align="center" justify="between" gap="2" class="summary__row">
      <Text type="body-sm" is="span">Investment Value</Text>
      <Text type="body-sm" weight="semibold" is="span">{{ formatAed(grossAmount) }}</Text>
    </Box>
    <Box class="summary__divider" />
    <Box align="center" justify="between" gap="2" class="summary__row">
      <Box align="center" gap="1" class="summary__label-row">
        <Text type="body-sm" is="span">Total Applicable Fee</Text>
        <PiIcon name="info-circle" class="summary__info-icon" @click="feePopover?.toggle($event)" />
      </Box>
      <Text type="body-sm" weight="semibold" is="span">{{ formatAed(totalFeeAmount) }}</Text>
    </Box>
    <Popover ref="feePopover">
      <BreakdownPopover title="Fees Breakdown" :rows="feeRows" />
    </Popover>
    <Box class="summary__divider" />
    <Box align="center" justify="between" gap="2" class="summary__row">
      <Text
        type="body-sm"
        weight="semibold"
        is="span"
        transform="uppercase"
        class="summary__total-label"
      >
        TOTAL PAYABLE
      </Text>
      <Text type="heading" is="span" color="var(--p-info-700)">
        {{ formatAed(netAmount) }}
      </Text>
    </Box>
    <Box v-if="exceedsTokens" align="start" gap="2" class="summary__alert summary__alert--error">
      <Text is="span" class="summary__alert-icon">⚠️</Text>
      <Text type="body-sm" weight="semibold" is="span" class="summary__alert-text">
        Only {{ tokenFormatter.format(tokensAvailable) }} tokens available.
      </Text>
    </Box>
  </Box>
</template>

<style scoped>
.summary {
  gap: 12px;
  padding: 16px 18px;
  border: 1px solid var(--p-content-border-color);
  border-radius: 12px;
  background: var(--p-surface-0);
  transition: opacity 0.15s;
}

.summary--fetching {
  opacity: 0.6;
}

.summary__info-icon {
  font-size: 13px;
  color: var(--p-surface-400);
  cursor: pointer;
}

.summary__divider {
  height: 1px;
  background: var(--p-surface-100);
}

.summary__total-label {
  letter-spacing: 0.04em;
}

.summary__alert {
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid;
}

.summary__alert--error {
  background: var(--p-danger-50);
  border-color: var(--p-danger-500);
}

.summary__alert-icon {
  font-size: 13px;
  line-height: 20px;
  flex-shrink: 0;
}

.summary__alert-text {
  line-height: 20px;
  color: var(--p-danger-700);
}
</style>
